import React, { useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import Modal from 'react-modal';
import moment from 'moment-timezone';

import ProviderDetails from '../../../components/ProviderDetails';
import ProfileHeader from '../../../components/ProfileHeader';

import {
  resolveProviderServices,
  resolveProviderFundingTypes,
  resolveProviderAgeGroups
} from '../../../util/transforms';
import { PageNotFoundInner } from '../../Static/PageNotFound';
import { AgeGroup, FundingType, Provider, Service } from '../../../state/types';
import { useGetProviderDetailsQuery, useGetProviderDetailsUsingSlugQuery } from '../../../services/karista';

import {
  Card,
  CardInfoText,
  CallLink,
  ModalSection,
  CloseButton,
  modalStyles
} from './styles'

import callIcon from './static/icon-phone.png';
import chatIcon from './static/icon-livechat.png';

const iconStyle = {
  width: '20px',
  height: '20px',
};

export interface DetailProps extends RouteComponentProps<{id?: string, slug?: string}> {
  services?: Service[],
  fundingTypes?: {[k: string]: FundingType},
  ageGroups?: {[k: string]: AgeGroup},
};

interface InnerDetailProps extends Omit<DetailProps, 'fundingTypes' | 'services' | 'ageGroups' | 'location'> {
  selectedProvider: Omit<Provider, 'services' | 'age_groups' | 'funding_types'> & {services: Service[], age_groups: AgeGroup[], funding_types: FundingType[]},
  isProviderError: boolean,
  isLoading: boolean,
};

const useDetailProps = (props: DetailProps): InnerDetailProps => {
  const {
    match: {
      params: { id: providerId, slug: providerSlug },
    },
    services,
    fundingTypes,
    ageGroups,
  } = props;

  // const {data: rawProvider, isError, isLoading} = useGetProviderDetailsQuery(parseInt(providerId as string));
  const {data: rawProvider, isError, isLoading} = useGetProviderDetailsUsingSlugQuery(providerSlug as string);
  let selectedProvider = resolveProviderServices(services, rawProvider);
  selectedProvider = resolveProviderFundingTypes(fundingTypes, selectedProvider);
  selectedProvider = resolveProviderAgeGroups(ageGroups, selectedProvider);
  return {
    ...props,
    // TODO: The below cast is only here because of the jumbling up of API types and transformed types that goes into
    // providers. I'll add a ticket to clean this up, then this can go.
    selectedProvider: selectedProvider as (Omit<Provider, 'services' | 'age_groups' | 'funding_types'> & {services: Service[], age_groups: AgeGroup[], funding_types: FundingType[]}),
    isProviderError: isError,
    isLoading,
  };
}

const Detail: React.FC<DetailProps> = props => {
  const {
    selectedProvider: {
      services,
      funding_types: fundingTypes,
      age_groups: ageGroups,
      id: id,
      logo_url: logoUrl,
      overall_review: review,
      reviews_count: reviewsCount,
      is_active: isActive,
      url_slug: slug,
      ...providerRest
    },
    isProviderError,
    isLoading,
  } = useDetailProps(props);

  // State to manage the visibility of the help popup
  const [showHelpPopup, setShowHelpPopup] = useState(false);

  useEffect(() => {
    // Get the start of the current day in Melbourne time zone
    const startOfDay = moment().tz('Australia/Melbourne').startOf('day');
  
    const businessHoursStart = startOfDay.clone().set({ hour: 9, minute: 0 }).valueOf(); // 9:00 AM in Melbourne time
    const businessHoursEnd = startOfDay.clone().set({ hour: 17, minute: 30 }).valueOf(); // 5:30 PM in Melbourne time

    // Get current time in Melbourne time zone
    const currentTime = moment().tz('Australia/Melbourne');
    const dayOfWeek = currentTime.day();
    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
    const currentTimeValue = currentTime.valueOf();

  
    // Check if current time falls within business hours
    const isWithinBusinessHours = isWeekday && (currentTimeValue >= businessHoursStart && currentTimeValue <= businessHoursEnd);
  
    // Calculate remaining time until the end of business hours
    let remainingTime = 0;
    if (isWithinBusinessHours) {
      remainingTime = businessHoursEnd - currentTimeValue;
    }
  
    // Set a timeout to show the help popup after 25 seconds
    const timeout = setTimeout(() => {
      if (remainingTime <= 0) {
        setShowHelpPopup(false);
      } else {
        setShowHelpPopup(true);
      }
    }, 25000);
  
    // Clear the timeout on component unmount
    return () => clearTimeout(timeout);
  }, []);

  const closePopup = () => {
    // Close the popup
    setShowHelpPopup(false);
  };

  const openIntercom = e => {
    e.preventDefault();
    (window as any).Intercom(
      'showNewMessage',
      `Hi there, how can we help`
    );
  };

  if (isProviderError) {
    return <PageNotFoundInner />;
  }

  // TODO: We should only need "isLoading".
  if (!id || isLoading) {
    return <div>Loading ...</div>;
  }

  return (
    <>
    <ProfileHeader
      logoUrl={logoUrl}
      providerId={id}
      hasReviews={false}
      review={review}
      purple
      reviewsCount={reviewsCount}
      isActive={isActive}
      providerSlug={slug}
      {...providerRest}
    >
      <ProviderDetails
        {...providerRest}
        services={services}
        fundingTypes={fundingTypes}
        providerId={id}
        isActive={isActive}
        providerSlug={slug}
      />
    </ProfileHeader>
      {showHelpPopup && (<Modal
        isOpen={showHelpPopup}
        onRequestClose={closePopup}
        contentLabel="Wait time Popup"
        style={modalStyles}
      >
        <CloseButton onClick={closePopup}>x</CloseButton>
         <ModalSection>
          <p>
          Karista's Client Services 
          team are experienced in finding and connecting NDIS and HCP participants 
          to services based on your personal needs and goals. Speak to our Client Services Team now.
          </p>
          <br></br>
          <Card>
            <img src={callIcon} alt="Call icon" style={iconStyle} />
            <CallLink href="tel:+611300274782">
              <CardInfoText>Call us on 1300 274 782</CardInfoText>
            </CallLink>
          </Card>
            <br/>
          <Card onClick={openIntercom}>
          <img src={chatIcon} alt="Chat icon" style={iconStyle} />
            <CardInfoText>Live Chat</CardInfoText>
          </Card>
           </ModalSection>
      </Modal>)}
  </>
  );
}

export default Detail;
